import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Save, X, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api";

const EMPTY = { email: "", password: "", role: "owner", museum_ids: [], active: true, full_name: "" };

export default function Users() {
  const [items, setItems] = useState([]);
  const [museums, setMuseums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([api.adminListUsers(), api.adminListMuseums()]);
      setItems(u.items || []); setMuseums(m.items || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const body = { ...editing };
    if (!body.password) delete body.password;
    try {
      if (editing.id) await api.adminUpdateUser(editing.id, body);
      else await api.adminCreateUser(body);
      toast.success("Salvato");
      setEditing(null); load();
    } catch (e) { toast.error("Salvataggio fallito", { description: e.message }); }
  }

  async function remove(id) {
    if (!window.confirm("Eliminare utente?")) return;
    try { await api.adminDeleteUser(id); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e.message); }
  }

  return (
    <div className="p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-4xl text-ink">Utenti admin</h1>
          <p className="text-sm text-ink-soft mt-1">{items.length} account totali</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2.5 rounded-full font-semibold transition">
          <Plus size={18} /> Nuovo utente
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" /></div>
      ) : (
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg/50">
              <tr className="text-left">
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">EMAIL</th>
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">RUOLO</th>
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">SCOPE</th>
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">STATO</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-border hover:bg-bg/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-brand/10 text-brand flex items-center justify-center"><User size={14} /></div>
                      <div>
                        <div className="font-semibold text-ink">{u.email}</div>
                        {u.full_name && <div className="text-xs text-ink-soft">{u.full_name}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold tracking-wider ${u.role === "super" ? "bg-brand/10 text-brand" : "bg-gold/15 text-gold"}`}>
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-soft">{u.role === "super" ? "Tutti" : `${u.museum_ids?.length || 0} musei`}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {u.active ? "ATTIVO" : "DISATTIVO"}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-1.5 justify-end">
                    <button onClick={() => setEditing({ ...u, password: "" })} className="w-8 h-8 rounded-lg bg-bg hover:bg-brand/10 hover:text-brand flex items-center justify-center transition"><Edit2 size={14} /></button>
                    <button onClick={() => remove(u.id)} className="w-8 h-8 rounded-lg bg-bg hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-heading font-bold text-2xl text-ink">{editing.id ? "Modifica" : "Nuovo"} Utente</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-bg"><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">EMAIL</label>
                <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="form-input" />
              </div>
              <div>
                <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">PASSWORD {editing.id && "(vuoto = invariata)"}</label>
                <input type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="form-input" />
              </div>
              <div>
                <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">NOME COMPLETO</label>
                <input value={editing.full_name || ""} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className="form-input" />
              </div>
              <div>
                <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">RUOLO</label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="form-input">
                  <option value="super">Super (full access)</option>
                  <option value="owner">Owner (museo specifico)</option>
                </select>
              </div>
              {editing.role === "owner" && (
                <div>
                  <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">MUSEI ASSEGNATI</label>
                  <div className="border border-border rounded-xl max-h-48 overflow-auto bg-white">
                    {museums.map((m) => (
                      <label key={m.id} className="flex items-center gap-2 px-3 py-2 hover:bg-bg cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={(editing.museum_ids || []).includes(m.id)}
                          onChange={(e) => {
                            const ids = new Set(editing.museum_ids || []);
                            if (e.target.checked) ids.add(m.id); else ids.delete(m.id);
                            setEditing({ ...editing, museum_ids: Array.from(ids) });
                          }}
                        />
                        {m.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Account attivo
              </label>
            </div>
            <div className="flex gap-2 justify-end px-6 py-4 border-t border-border">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-bg text-ink-soft font-medium hover:bg-border transition">Annulla</button>
              <button onClick={save} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2 rounded-xl font-semibold transition"><Save size={16} /> Salva</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.form-input{width:100%;padding:.65rem .9rem;border:1px solid hsl(var(--border));border-radius:.7rem;background:#fff;font-size:.875rem;color:#1c1c1e;outline:none;transition:.15s}.form-input:focus{border-color:#0B4A99}`}</style>
    </div>
  );
}
